<template>
    <div>
        <div class="g-mt-5">
            <g-tooltip v-if="filterDisabled" :title="filterDisabledMessage">
                <span>
                    <g-button
                        class="g-mr-3"
                        shape="round"
                        theme="live"
                        :disabled="filterDisabled"
                        @click="showFilterDrawer"
                    >
                        <g-typography font-weight="semibold">{{ filterButtonText }}</g-typography>
                    </g-button>
                </span>
            </g-tooltip>
            <g-button v-else class="g-mr-3" shape="round" theme="live" @click="showFilterDrawer">
                <g-typography font-weight="semibold">{{ filterButtonText }}</g-typography>
            </g-button>

            <g-tooltip v-if="exportDisabled" :title="exportDisabledMessage">
                <span>
                    <g-button shape="round" theme="live" :disabled="exportDisabled" @click="handleOpenExportDrawer">
                        <g-typography font-weight="semibold">Export</g-typography>
                    </g-button>
                </span>
            </g-tooltip>
            <g-button v-else shape="round" theme="live" @click="handleOpenExportDrawer">
                <g-typography font-weight="semibold">Export</g-typography>
            </g-button>
        </div>

        <div v-if="redirectPaymentsToAgent" class="agent-notice">
            Your payments are currently being sent to your agent on your behalf. To change this, visit
            <router-link :to="{ name: 'edit.profile' }"> agent settings. </router-link>
        </div>

        <g-base-box class="g-mt-5" padding="none">
            <g-tabs :active-key="currentTab" :tab-bar-gutter="5" @change="handleTabChange">
                <g-tab-pane :key="PAYMENTS_TAB" tab="Payments" />
                <g-tab-pane :key="CONVERSIONS_TAB" tab="Conversions" />
            </g-tabs>
            <g-loader v-if="isLoading" class="loading-icon" type="block" />
            <div v-else>
                <div v-if="paymentsPageData && currentTab === PAYMENTS_TAB">
                    <div class="total-bar">
                        <g-media-object>
                            <template #left>
                                <g-typography tag="p" value="Total" />
                            </template>

                            <template #right>
                                <g-typography tag="p" :value="paymentsTotal" />
                            </template>
                        </g-media-object>
                    </div>
                    <div v-for="(payment, index) in paymentsPageData.data" :key="payment.id">
                        <payment-clickable-list-item
                            :is-conversion="false"
                            :payout="payment"
                            :handle-select="() => handleSelectPayment(payment)"
                            :is-last="index === paymentsPageData.data.length - 1"
                        />
                    </div>
                    <div
                        v-if="paymentsPageData.total === 0"
                        class="empty-state g-p-5"
                        :style="`height: ${getEmptyStateHeight}px`"
                    >
                        <span>{{
                            filters.startDate || filters.endDate
                                ? 'You do not have any payouts in the chosen date range.'
                                : 'You do not have any payouts yet.'
                        }}</span>
                    </div>
                </div>
                <div v-else-if="conversionsPageData && currentTab === CONVERSIONS_TAB">
                    <div class="total-bar">
                        <g-media-object>
                            <template #left>
                                <g-typography tag="p" value="Total" />
                            </template>

                            <template #right>
                                <g-typography tag="p" :value="conversionsTotal" />
                            </template>
                        </g-media-object>
                    </div>
                    <g-loader v-if="isLoading" class="loading-icon" type="block" />
                    <div v-for="(conversion, index) in conversionsPageData.data" :key="conversion.id">
                        <payment-clickable-list-item
                            :is-conversion="true"
                            :payout="conversion"
                            :handle-select="() => handleSelectConversion(conversion)"
                            :is-last="index === conversionsPageData.data.length - 1"
                        />
                    </div>
                    <div
                        v-if="conversionsPageData.total === 0"
                        class="empty-state g-p-5"
                        :style="`height: ${getEmptyStateHeight}px`"
                    >
                        <span>{{
                            filters.startDate || filters.endDate
                                ? 'You do not have any conversions in the chosen date range.'
                                : 'You do not have any conversions yet.'
                        }}</span>
                    </div>
                </div>
            </div>
        </g-base-box>

        <div class="pagination-container">
            <g-pagination
                v-if="currentTab === PAYMENTS_TAB"
                class="g-mx-auto"
                size="small"
                :current="paymentsPage"
                :total="paymentsPageData ? paymentsPageData.total : 0"
                :page-size="paymentsPageData ? paymentsPageData.per_page : 10"
                :disabled="isLoading"
                show-less-items
                @change="setPaymentsPage"
            />
            <g-pagination
                v-else
                class="g-mx-auto"
                size="small"
                :current="conversionsPage"
                :total="conversionsPageData ? conversionsPageData.total : 0"
                :page-size="conversionsPageData ? conversionsPageData.per_page : 10"
                :disabled="isLoading"
                show-less-items
                @change="setConversionsPage"
            />
        </div>

        <payments-and-conversions-export-drawer
            :visible="isExportDrawerVisible"
            :total="exportTotal"
            :filter-start-date="filters.startDate"
            :filter-end-date="filters.endDate"
            :tab="currentTab"
            @close="hideExportDrawer"
        />

        <payments-and-conversions-filter-drawer
            :visible="isFilterDrawerVisible"
            @set-filter-button-text-override="setFilterButtonTextOverride"
            @apply-filters="applyFilters"
            @close="hideFilterDrawer"
        />

        <payment-details-drawer
            :visible="isPaymentDetailsDrawerVisible"
            :data="selectedData"
            @close="hidePaymentDetailsDrawer"
        />

        <conversion-details-drawer
            :visible="isConversionDetailsDrawerVisible"
            :data="selectedData"
            @close="hideConversionDetailsDrawer"
        />
    </div>
</template>

<script>
    import { defineComponent, ref, onMounted, computed } from 'vue-demi';
    import usePayments from '../../../../composables/use-payments';
    import useConversions from '../../../../composables/use-conversions';
    import useToggle from '../../../../composables/use-toggle';
    import { useContactStore, useCreatorStore } from '../../../../../stores';
    import { message } from '@grininc/grin-ui';
    import PaymentsAndConversionsExportDrawer from './PaymentsAndConversionsExportDrawer';
    import PaymentsAndConversionsFilterDrawer from './PaymentsAndConversionsFilterDrawer';
    import PaymentDetailsDrawer from './PaymentDetailsDrawer';
    import ConversionDetailsDrawer from './ConversionDetailsDrawer';
    import PaymentClickableListItem from './PaymentClickableListItem';
    import { PAYMENTS_TAB, CONVERSIONS_TAB } from '../../../../../app-v2/constants/payouts';
    import moment from 'moment';

    export default defineComponent({
        name: 'PaymentsAndConversionsView',
        components: {
            PaymentClickableListItem,
            ConversionDetailsDrawer,
            PaymentDetailsDrawer,
            PaymentsAndConversionsFilterDrawer,
            PaymentsAndConversionsExportDrawer
        },

        setup() {
            //store data
            const contact = useContactStore().data;
            const creator = useCreatorStore();

            //page state
            const isLoading = ref(false);

            //tab state
            const currentTab = ref(PAYMENTS_TAB);

            //pagination state
            const conversionsPage = ref(1);
            const paymentsPage = ref(1);

            //filter state
            const filters = ref({});
            const filterButtonTextOverride = ref('');
            const filterButtonText = computed(() => {
                if (filterButtonTextOverride.value) {
                    return `Filter: ${filterButtonTextOverride.value}`;
                } else if (filters.value?.startDate && filters.value?.endDate) {
                    return `Filter: ${moment(filters.value?.startDate).format('MM/DD/YY')}-${moment(
                        filters.value?.endDate
                    ).format('MM/DD/YY')}`;
                } else if (filters.value?.startDate && !filters.value?.endDate) {
                    return `Filter: after ${moment(filters.value?.startDate).format('MM/DD/YY')}`;
                } else if (!filters.value?.startDate && filters.value?.endDate) {
                    return `Filter: before ${moment(filters.value?.endDate).format('MM/DD/YY')}`;
                } else {
                    return 'Filter';
                }
            });

            //drawer state
            const {
                currentValue: isFilterDrawerVisible,
                setTrue: showFilterDrawer,
                setFalse: hideFilterDrawer
            } = useToggle(false);

            const {
                currentValue: isExportDrawerVisible,
                setTrue: showExportDrawer,
                setFalse: hideExportDrawer
            } = useToggle(false);

            const {
                currentValue: isPaymentDetailsDrawerVisible,
                setTrue: showPaymentDetailsDrawer,
                setFalse: hidePaymentDetailsDrawer
            } = useToggle(false);

            const {
                currentValue: isConversionDetailsDrawerVisible,
                setTrue: showConversionDetailsDrawer,
                setFalse: hideConversionDetailsDrawer
            } = useToggle(false);

            //api data
            const conversionsPageData = ref(null);
            const conversionsTotal = ref(0);

            const paymentsPageData = ref(null);
            const paymentsTotal = ref(0);

            const selectedData = ref(null);
            const exportTotal = ref(null);

            //helper methods
            const loadData = async () => {
                isLoading.value = true;

                Promise.all([
                    loadPaymentsPromise(),
                    loadConversionsPromise(),
                    loadPaymentsTotalPromise(),
                    loadConversionsTotalPromise()
                ])
                    .then((res) => {
                        paymentsPageData.value = res[0]?.data;
                        conversionsPageData.value = res[1]?.data;
                        paymentsTotal.value = res[2]?.data?.total;
                        conversionsTotal.value = res[3]?.data?.total;
                    })
                    .catch(() => {
                        message.error('There was an error while trying to load your payouts, please try again later.');
                        isLoading.value = false;
                    })
                    .finally(() => (isLoading.value = false));
            };

            const loadPaymentsPromise = () => {
                return usePayments().getPayments(
                    contact.id,
                    filters.value?.startDate,
                    filters.value?.endDate,
                    paymentsPage.value
                );
            };

            const loadConversionsPromise = () => {
                return useConversions().getConversions(
                    contact.id,
                    filters.value?.startDate,
                    filters.value?.endDate,
                    conversionsPage.value
                );
            };

            const loadConversionsTotalPromise = () => {
                return useConversions().getConversionsTotal(
                    contact.id,
                    filters.value?.startDate,
                    filters.value?.endDate
                );
            };

            const loadPaymentsTotalPromise = () => {
                return usePayments().getPaymentsTotal(contact.id, filters.value?.startDate, filters.value?.endDate);
            };

            const loadPayments = async () => {
                isLoading.value = true;

                try {
                    const paymentsResult = await loadPaymentsPromise();
                    paymentsPageData.value = paymentsResult.data;
                } catch (e) {
                    message.error('There was an error while trying to load your payments, please try again later.');
                }

                isLoading.value = false;
            };

            const loadConversions = async () => {
                isLoading.value = true;

                try {
                    const conversionsResult = await loadConversionsPromise();
                    conversionsPageData.value = conversionsResult.data;
                } catch (e) {
                    message.error('There was an error while trying to load your conversions, please try again later.');
                }

                isLoading.value = false;
            };

            const setPaymentsPage = (page) => {
                paymentsPage.value = page;
                loadPayments();
            };

            const setConversionsPage = (page) => {
                conversionsPage.value = page;
                loadConversions();
            };

            const handleTabChange = (key) => {
                currentTab.value = key;
            };

            const handleSelectPayment = (data) => {
                selectedData.value = data;
                showPaymentDetailsDrawer();
            };

            const handleSelectConversion = (data) => {
                selectedData.value = data;
                showConversionDetailsDrawer();
            };

            const handleOpenExportDrawer = () => {
                if (isLoading.value) {
                    return;
                }

                if (currentTab.value === PAYMENTS_TAB) {
                    exportTotal.value = paymentsPageData.value?.total;
                } else {
                    exportTotal.value = conversionsPageData.value?.total;
                }

                showExportDrawer();
            };

            const applyFilters = (newFilters) => {
                filters.value = newFilters;
                loadData();
            };

            const setFilterButtonTextOverride = (text) => {
                filterButtonTextOverride.value = text;
            };

            const itemHeight = 109;
            const getEmptyStateHeight = computed(() => {
                if (currentTab.value === PAYMENTS_TAB) {
                    return conversionsPageData.value?.data?.length > 1
                        ? conversionsPageData.value?.data?.length * itemHeight
                        : itemHeight;
                } else {
                    return paymentsPageData.value?.data?.length > 1
                        ? paymentsPageData.value?.data?.length * itemHeight
                        : itemHeight;
                }
            });

            const hasConversions = computed(() => {
                return conversionsPageData.value?.total > 0;
            });

            const hasPayments = computed(() => {
                return paymentsPageData.value?.total > 0;
            });

            const redirectPaymentsToAgent = computed(() => {
                return creator.data.creator?.is_paid_to_agent;
            });

            const isFiltered = computed(() => {
                return !!(filters.value?.startDate || filters.value?.endDate);
            });

            const filterDisabled = computed(() => {
                return (
                    isLoading.value ||
                    (currentTab.value === CONVERSIONS_TAB && !isFiltered.value && !hasConversions.value) ||
                    (currentTab.value === PAYMENTS_TAB && !isFiltered.value && !hasPayments.value)
                );
            });

            const filterDisabledMessage = computed(() => {
                if (currentTab.value === CONVERSIONS_TAB) {
                    return 'There are no conversions to filter.';
                }

                return 'There are no payouts to filter.';
            });

            const exportDisabled = computed(() => {
                return (
                    isLoading.value ||
                    (currentTab.value === CONVERSIONS_TAB && !hasConversions.value) ||
                    (currentTab.value === PAYMENTS_TAB && !hasPayments.value)
                );
            });

            const exportDisabledMessage = computed(() => {
                if (currentTab.value === CONVERSIONS_TAB) {
                    return 'There are no conversions to export.';
                }

                return 'There are no payouts to export.';
            });

            //lifecycle hooks
            onMounted(() => {
                loadData();
            });

            return {
                isLoading,

                //tab state
                currentTab,
                PAYMENTS_TAB,
                CONVERSIONS_TAB,

                //pagination state
                paymentsPage,
                conversionsPage,
                setPaymentsPage,
                setConversionsPage,

                //filter state
                applyFilters,
                filters,
                filterButtonText,
                filterButtonTextOverride,
                setFilterButtonTextOverride,

                //drawer state
                isExportDrawerVisible,
                hideExportDrawer,
                showExportDrawer,

                isFilterDrawerVisible,
                hideFilterDrawer,
                showFilterDrawer,

                isPaymentDetailsDrawerVisible,
                hidePaymentDetailsDrawer,
                showPaymentDetailsDrawer,

                isConversionDetailsDrawerVisible,
                hideConversionDetailsDrawer,
                showConversionDetailsDrawer,

                //data
                conversionsPageData,
                conversionsTotal,
                paymentsPageData,
                paymentsTotal,
                selectedData,
                exportTotal,
                redirectPaymentsToAgent,

                //helper methods
                handleTabChange,
                handleSelectConversion,
                handleSelectPayment,
                handleOpenExportDrawer,
                getEmptyStateHeight,
                filterDisabled,
                filterDisabledMessage,
                exportDisabled,
                exportDisabledMessage
            };
        }
    });
</script>

<style lang="scss" scoped>
    ::v-deep .ant-tabs-nav {
        width: 100% !important;
    }

    ::v-deep .ant-tabs-tab {
        flex-grow: 1;
        margin-right: 0px !important;
        text-align: center;
    }

    ::v-deep .ant-tabs-bar {
        margin-bottom: 0;
    }

    ::v-deep .ant-tabs-nav > div:nth-of-type(1) {
        display: flex !important;
        width: 100% !important;
    }

    .pagination-container {
        display: flex;
        padding-top: 1rem;
    }

    .agent-notice {
        margin-top: 1rem;
        padding: 1rem 1.5rem;
        background-color: $g-color-grey-3;
        border-width: 1px;
        border-color: $g-color-grey-4;
        border-radius: $g-border-radius-md;

        a,
        a:visited,
        a:active,
        a:hover {
            color: #4d4fc8;
            text-decoration: underline;
        }
    }

    .total-bar {
        padding: $g-spacing-3 $g-spacing-5;
        background-color: $g-color-grey-3;
        border-bottom: 1px solid $g-color-grey-4;
    }

    .loading-icon {
        height: 40vh;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .empty-state {
        height: 109px;
        display: flex;
        align-items: center;
    }
</style>
